import attractionsIcon from "@assets/icons/attractions.svg";
import calendarClockIcon from "@assets/icons/calendar_clock.svg";
import conversionPathIcon from "@assets/icons/conversion_path.svg";
import distanceIcon from "@assets/icons/distance.svg";
import AppBar from "@components/AppBar";
import { InteractiveChatTextInput } from "@components/InteractiveChat/InteractiveChatTextInput";
import LocationPermissionModal from "@components/LocationPermissionModal";
import OptionCard from "@components/OptionCard";
import ROUTES from "@constants/routing";
import { InputContext } from "@context/Input/InputContext";
import { UserContext } from "@context/User/UserContext";
import { useHeaderHeight } from "@hooks/useHeaderHeight";
import LayoutDefault from "@layouts/LayoutDefault";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { chatStreamingService } from "@services/chatStreamingApi";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import LoginPrompt from "./LoginPrompt";
import { LocationInfoTracker } from "../../services/utils/locationInfoTracker";

const WelcomeView: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();

  // Use global contexts
  const inputCtx = React.useContext(InputContext);
  const userCtx = React.useContext(UserContext);

  // Hooks must be called before any conditional returns
  const headerHeight = useHeaderHeight();

  // Local state for the welcome input (moved before conditional returns)
  const [input, setInput] = React.useState("");
  const [audioBlob, setAudioBlob] = React.useState<Blob | null>(null);
  const [showLocationModal, setShowLocationModal] = React.useState(false);

  // Get user name for personalized greeting
  const userName = userCtx?.user?.name || "";

  // Show location info modal only once per user
  React.useEffect(() => {
    if (userCtx?.user && !LocationInfoTracker.hasSeenLocationInfo()) {
      // Show info modal only if user hasn't seen it before
      setShowLocationModal(true);
    }
  }, [userCtx?.user]);

  const handleLocationAccept = () => {
    // Mark that user has seen the info modal
    LocationInfoTracker.markLocationInfoSeen();
    // Close the modal
    setShowLocationModal(false);

    // Try to get location (this will trigger browser permission if needed)
    chatStreamingService
      .getCurrentLocation()
      .then((coordinates) => {
        if (coordinates) {
          userCtx?.setLocationPermission("granted");
        } else {
          userCtx?.setLocationPermission("denied");
        }
      })
      .catch((_error) => {
        userCtx?.setLocationPermission("denied");
      });
  };

  const handleLocationModalClose = () => {
    setShowLocationModal(false);
  };

  // 1. Mientras se comprueba la sesión, mostrar un spinner
  if (userCtx?.isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh"
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // 2. Si no hay usuario, mostrar el componente de login
  if (!userCtx?.user) {
    return <LoginPrompt />;
  }

  // 3. Si hay usuario, mostrar el contenido normal de WelcomeView

  const handleStartChat = (filterType: string) => {
    navigate(`${ROUTES.FILTER_SECTION}?type=${filterType}`);
  };

  const handleSubmitFromInput = () => {
    if (!inputCtx) return;
    if (input.trim() || audioBlob) {
      inputCtx.setInitialMessage(input.trim());
      inputCtx.setInitialAudio(audioBlob);
      navigate(ROUTES.CHATBOT);
    }
  };

  const handleRecord = (blob: Blob) => {
    setAudioBlob(blob);
    if (inputCtx) {
      inputCtx.setInitialMessage("");
      inputCtx.setInitialAudio(blob);
      navigate(ROUTES.CHATBOT);
    }
  };

  const handleDeleteAudio = () => {
    setAudioBlob(null);
  };

  const _handleBackFromChat = () => {
    if (!inputCtx) return;
    inputCtx.setInitialMessage("");
    inputCtx.setInitialAudio(null);
    setInput("");
    setAudioBlob(null);
  };

  const optionCards = [
    {
      id: "location",
      title: t(
        "welcome.search_by_area",
        "Text exemple fent referència a buscar per zona"
      ),
      iconSrc: distanceIcon,
      iconAlt: "Buscar por zona",
      onClick: () => handleStartChat("location")
    },
    {
      id: "day",
      title: t(
        "welcome.search_by_day",
        "Text exemple fent referència a buscar per dia"
      ),
      iconSrc: calendarClockIcon,
      iconAlt: "Buscar por día",
      onClick: () => handleStartChat("day")
    },
    {
      id: "activity",
      title: t(
        "welcome.search_by_activity",
        "Text exemple fent referència a buscar per activitat"
      ),
      iconSrc: attractionsIcon,
      iconAlt: "Buscar por actividad",
      onClick: () => handleStartChat("activity")
    },
    {
      id: "combinations",
      title: t(
        "welcome.search_by_combinations",
        "Text exemple fent referència a buscar per combinacions"
      ),
      iconSrc: conversionPathIcon,
      iconAlt: "Buscar por combinaciones",
      onClick: () => handleStartChat("combinations")
    }
  ];

  return (
    <>
      <LayoutDefault title="diari-ara">
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: "100%",
            backgroundColor: theme.palette.customColors.welcomeBackground,
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        >
          <Box width={"100%"}>
            <AppBar />

            {
              <Box
                sx={{
                  flexGrow: 1,
                  overflow: "hidden",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: { xs: "flex-start", sm: "space-between" },
                  px: { xs: 2, sm: 4 },
                  pb: { xs: 0, sm: 4 },
                  boxSizing: "border-box",
                  position: "relative",
                  pt: {
                    xs: headerHeight.mobile,
                    sm: headerHeight.desktop
                  }
                }}
              >
                {/* Main Content Block - Centered vertically on desktop, separated on mobile */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                    flex: { xs: 1, sm: 1 },
                    justifyContent: { xs: "flex-start", sm: "center" }
                  }}
                >
                  {/* Welcome Message - Almost at the top on mobile, above cards on desktop */}
                  <Box
                    sx={{
                      textAlign: "center",
                      mb: { xs: 3, sm: 4 },
                      mt: { xs: 1, sm: 0 },
                      width: 362,
                      height: "auto",
                      px: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: { xs: 1.5, sm: 3 }
                    }}
                  >
                    <Typography
                      component="h3"
                      sx={{
                        fontSize: 25,
                        fontWeight: 600,
                        textAlign: "center",
                        color: theme.palette.customColors.textPrimary,
                        mb: 0
                      }}
                    >
                      {userName
                        ? t("welcome.start_chatting_personalized", {
                            name: userName
                          })
                        : t("welcome.start_chatting")}
                    </Typography>
                    <Typography
                      component="div"
                      sx={{
                        fontFamily: theme.typography.fontFamily,
                        fontSize: { xs: "16px", sm: 16 },
                        fontWeight: 325,
                        lineHeight: { xs: "20px", sm: "normal" },
                        letterSpacing: { xs: "0%", sm: "normal" },
                        color: {
                          xs: theme.palette.customColors.textSecondary,
                          sm: theme.palette.customColors.welcomeTextColor
                        },
                        textAlign: "center",
                        mb: 0
                      }}
                    >
                      {t("welcome.filter_message")}
                    </Typography>
                  </Box>

                  {/* Option Cards Container - Centered vertically on mobile, below text on desktop */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: { xs: "center", sm: "flex-start" },
                      width: "100%",
                      flex: { xs: 1, sm: 0 }
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "repeat(2, minmax(140px, 179px))",
                          sm: "repeat(2, 200px)",
                          md: "repeat(4, 200px)"
                        },
                        gap: 1.5,
                        width: {
                          xs: "100%",
                          sm: "fit-content",
                          md: "fit-content"
                        },
                        maxWidth: {
                          xs: "370px",
                          sm: "none",
                          md: "none"
                        },
                        margin: {
                          xs: "0 auto",
                          sm: "0",
                          md: "0"
                        },
                        justifyItems: {
                          xs: "stretch",
                          sm: "stretch",
                          md: "stretch"
                        }
                      }}
                    >
                      {optionCards.map((card) => (
                        <OptionCard
                          key={card.id}
                          title={card.title}
                          iconSrc={card.iconSrc}
                          iconAlt={card.iconAlt}
                          onClick={card.onClick}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>

                {/* Chat input at the bottom */}
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 800,
                    mt: { xs: "auto", sm: "auto" },
                    pb: { xs: 0, sm: 0 }
                  }}
                >
                  <InteractiveChatTextInput
                    label={t("chatbot.common.input_label")}
                    placeholder={t("chatbot.common.input_placeholder")}
                    value={input}
                    onChange={setInput}
                    onSubmit={handleSubmitFromInput}
                    disabled={false}
                    onRecord={handleRecord}
                    audioBlob={audioBlob}
                    onDeleteAudio={handleDeleteAudio}
                  />

                  {/* Disclaimer text */}
                  <Box sx={{ px: 0, mt: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: theme.typography.fontFamily,
                        fontSize: "14px",
                        fontWeight: 325,
                        lineHeight: "14px",
                        letterSpacing: "0%",
                        color: theme.palette.customColors.textTertiary,
                        textAlign: "center"
                      }}
                    >
                      {t("chatbot.disclaimer")}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            }
          </Box>
        </Box>
      </LayoutDefault>

      {/* Location Info Modal */}
      <LocationPermissionModal
        open={showLocationModal}
        onAccept={handleLocationAccept}
        onClose={handleLocationModalClose}
      />
    </>
  );
};

export default WelcomeView;
